import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { AppLayout } from '@/components/layout/AppLayout';
import { EmptyState } from '@/components/feedback/EmptyState';
import LoginPage from '@/features/auth/pages/LoginPage';

import { ProtectedRoute } from './ProtectedRoute';
import { DEFAULT_ROUTE, routes } from './routeConfig';

// The customer's order chat, linked from the vendor's Slack message. No sign-in: the order's
// unguessable guid in the URL is the key, and the endpoint returns no contact details.
const OrderChatPage = lazy(() => import('@/features/chat/pages/OrderChatPage'));

/**
 * Routes, generated from the same `routeConfig` the sidebar reads.
 *
 * Every element is code-split via React.lazy in routeConfig, so AG Grid ships in its own chunk
 * and is fetched only when a route that renders a grid is first opened.
 */
export function AppRouter() {
  return (
    <Routes>
      {/* Public, and deliberately outside AppLayout: no sidebar or header. */}
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/order-chat/:guid"
        element={
          <Suspense fallback={null}>
            <OrderChatPage />
          </Suspense>
        }
      />

      <Route element={<AppLayout />}>
        <Route index element={<Navigate to={DEFAULT_ROUTE} replace />} />

        {routes.map((route) => {
          const Component = route.component;
          return (
            <Route
              key={route.path}
              path={route.path}
              element={
                <ProtectedRoute>
                  <Component />
                </ProtectedRoute>
              }
            />
          );
        })}

        <Route
          path="*"
          element={
            <EmptyState
              title="Page not found"
              description="That URL does not match any screen. Use the sidebar to get back."
            />
          }
        />
      </Route>
    </Routes>
  );
}
